import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivityRecord, ActivityRecordParams } from '../../models';
import { ContextualHistoryService } from '../../services/contextual-history.service';
import { BehaviorSubject, fromEvent, merge, Observable, of, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, switchMap, take, tap } from 'rxjs/operators';
import { RelatedView, TransactionType } from '../../enums';
import { CategoryService, NoteMode, NoteService } from '../../../../../../../core/lists';
import { MatSelect } from '@angular/material/select';
import { TaskService } from '../../../../../../../core/timeline';
import { UserService } from '../../../../../../../core/auth/_services';
import { MatDialog } from '@angular/material/dialog';
import { CommentsService } from '../../../../../../../core/services/comments.service';
import { CommentsDialogComponent } from '../../../comments-dialog/comments-dialog.component';
import { CreateTaskComponent } from '../../../../../../pages/calendar/create-task/create-task.component';
import { AddNoteDialogComponent } from '../../../../../../pages/apps/lists/notes/add-note-dialog/add-note-dialog.component';
import { GeneralActionDialogComponent } from '../../../../crud/general-action-dialog/general-action-dialog.component';
import { FollowUpService } from '../../../../../../../core/services/follow-up.service';
import { TaskMode } from '../../../../../../pages/calendar/enums';
import { DatePipe } from '@angular/common';

@Component({
  selector: 'kt-activity-record-list',
  templateUrl: './activity-record-list.component.html',
  styleUrls: ['./activity-record-list.component.scss'],
})
export class ActivityRecordListComponent implements OnInit, OnDestroy {
  private subscriptions = new Subscription();
  TransactionType = TransactionType;
  transactionTypes = Object.keys(TransactionType);
  records: ActivityRecord[];
  monthsCount = 0;
  teams = [];
  isLoadMore = false;
  loading = new BehaviorSubject(false);
  loading$ = this.loading.asObservable();
  @ViewChild('search', { static: true }) searchInput: ElementRef;
  @ViewChild('loadMore', { static: true }) loadMoreBtn: ElementRef;
  @ViewChild('team', { static: true }) teamSelect: MatSelect;
  @ViewChild('type', { static: true }) typeSelect: MatSelect;

  constructor(
    public datepipe: DatePipe,
    private contextualHistory: ContextualHistoryService,
    private categoryService: CategoryService,
    private userService: UserService,
    private taskService: TaskService,
    private followUpService: FollowUpService,
    private commentsService: CommentsService,
    private noteService: NoteService,
    public dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    this.categoryService
      .getCategories()
      .pipe(take(1))
      .subscribe((res) => (this.teams = res.data));

    this.subscriptions.add(
      this.contextualHistory.relatedViewSub
        .pipe(
          filter((view) => !!RelatedView[view]),
          switchMap((view) => {
            this.loading.next(true);
            this.searchInput.nativeElement.value = '';
            this.teamSelect.value = '';
            this.typeSelect.value = 'false';

            return this.getActivityRecords$(view);
          }),
        )
        .subscribe((res) => {
          this.records = res.data.map((record) => ({
            ...record,
            status: record.status?.replace(/[ +_]/g, ' '),
            date_created: record.date_created ? new Date(`${record.date_created} UTC`) : '',
            date: record.date ? new Date(`${record.date} UTC`) : '',
          }));
          this.loading.next(false);
        }),
    );

    this.subscriptions.add(
      merge(
        fromEvent(this.searchInput.nativeElement, 'keyup').pipe(
          debounceTime(500),
          distinctUntilChanged(),
        ),
        fromEvent(this.loadMoreBtn.nativeElement, 'click').pipe(
          tap(() => (this.monthsCount = this.monthsCount + 1)),
        ),
        this.teamSelect.selectionChange,
        this.typeSelect.selectionChange,
      )
        .pipe(
          switchMap((event) => {
            if ((event as PointerEvent).type?.localeCompare('click') === 0) {
              this.isLoadMore = true;
            } else {
              this.isLoadMore = false;
              this.monthsCount = 0;
            }
            this.loading.next(true);

            return this.getActivityRecords$(this.contextualHistory.relatedViewSub.value);
          }),
        )
        .subscribe((res) => {
          const newRecords = res.data.map((record) => ({
            ...record,
            status: record.status?.replace(/[ +_]/g, ' '),
            date_created: record.date_created ? new Date(`${record.date_created} UTC`) : '',
            date: record.date ? new Date(`${record.date} UTC`) : '',
          }));

          this.records = this.isLoadMore ? [...this.records, ...newRecords] : newRecords;
          this.loading.next(false);
        }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private getActivityRecords$(view: RelatedView | ''): Observable<{ data: ActivityRecord[] }> {
    const query = {
      month: this.monthsCount,
      team_id: this.teamSelect.value,
      is_backstage: this.typeSelect.value,
      search_term: this.searchInput.nativeElement.value,
    };

    const args: [Partial<ActivityRecordParams>, number[]?] =
      view === RelatedView.CONTACT
        ? [{ contact_id: this.contextualHistory.contactIdSub.value, ...query }]
        : [query, this.contextualHistory.relatedPatientsIdSub.value];

    return this.contextualHistory.getActivityRecords(...args).pipe(take(1));
  }

  openTransaction(event: Event, activityRecord: ActivityRecord) {
    event.stopPropagation();

    let dialogRef;
    const requestAPI$ =
      activityRecord.transaction_type === TransactionType.TASK
        ? this.taskService.getTaskV3(activityRecord.transaction_id)
        : activityRecord.transaction_type === TransactionType.NOTE
        ? this.noteService.getNoteById(activityRecord.transaction_id)
        : of({ data: null });

    requestAPI$.pipe(take(1)).subscribe(({ data }) => {
      switch (activityRecord.transaction_type) {
        case TransactionType.TASK:
          dialogRef = this.dialog.open(CreateTaskComponent, {
            disableClose: true,
            closeOnNavigation: true,
            width: '95vw',
            maxWidth: '95vw',
            data: { mode: TaskMode.UPDATE, task: data },
          });

          break;

        case TransactionType.WORKFLOW_QUEUE:
          dialogRef = this.dialog.open(GeneralActionDialogComponent, {
            width: '450px',
            data: {
              title: 'Workflow Access',
              description:
                'Workflow access from contextual history is currently not available. Please access the workflow directly.',
              showConfirm: false,
              showCancel: true,
              cancelText: 'Close',
            },
          });

          break;

        case TransactionType.NOTE:
          dialogRef = this.dialog.open(AddNoteDialogComponent, {
            disableClose: true,
            closeOnNavigation: true,
            width: '95vw',
            maxWidth: '95vw',
            data: { mode: NoteMode.UPDATE, fromContact: false, note: data },
          });
      }

      dialogRef
        .afterClosed()
        .pipe(take(1))
        .subscribe(() =>
          this.getActivityRecords$(this.contextualHistory.relatedViewSub.value).subscribe(),
        );
    });
  }

  viewFollowUp(event: Event, activityRecord: ActivityRecord) {
    event.stopPropagation();

    const transactionType =
      activityRecord.transaction_type === TransactionType.WORKFLOW_QUEUE
        ? 'WORKFLOW'
        : activityRecord.transaction_type;

    this.followUpService
      .getFollowUpById(transactionType, activityRecord.transaction_id)
      .pipe(take(1))
      .subscribe((res) => {
        if (!res) {
          this.dialog.open(GeneralActionDialogComponent, {
            width: '450px',
            data: {
              title: 'Follow Up',
              description: `This ${transactionType.toLocaleLowerCase()} has no follow-up created.`,
              showConfirm: false,
              showCancel: true,
              cancelText: 'Close',
            },
          });

          return;
        }

        this.followUpService.openDialog({
          type: transactionType,
          title: activityRecord.title,
          followup: res,
        });
      });
  }

  viewComments(event: Event, activityRecord: ActivityRecord) {
    event.stopPropagation();

    const requestAPI$ =
      activityRecord.transaction_type === TransactionType.TASK
        ? this.commentsService.getComments('TASK', activityRecord.transaction_id)
        : activityRecord.transaction_type === TransactionType.WORKFLOW_QUEUE
        ? this.commentsService.getComments('workflow_queues', activityRecord.transaction_id)
        : of({ data: [] });

    requestAPI$.pipe(take(1)).subscribe(({ data }) => {
      this.dialog.open(CommentsDialogComponent, {
        width: '450px',
        data: {
          height: 40,
          transactionType:
            activityRecord.transaction_type === TransactionType.TASK ? 'TASK' : 'workflow_queues',
          transactionId: activityRecord.transaction_id,
          comments: data,
        },
      });
    });
  }
  getDate(created: any) {
    return this.datepipe.transform(created, 'yyyy-MM-dd HH:mm', 'UTC');
  }
}
